import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { CardContent, CircularProgress, Grid, TextField, withTheme } from "@material-ui/core";
import { CardPageItem, PageItem } from "../../../custom/Page";
import useListCustomersFinancialSummary from "../../../hooks/useListCustomersFinancialSummary.js";
import { CustomerListBySituationWithAccordion } from "./CustomersBySituation";
import { withRouter } from "react-router-dom";
import { FixedFooterPageItem, FooterItem } from "../FixedFooterPageItem.js";
import Number from "../../../custom/Number";
import SelectUserSituations from "../select/SelectUserSituations.js";
import { debounce, filterOnlyNumbers } from "../../../util/index.js";
import useRemoveArrowsNumberInput from "../../../util/useRemoveArrowsNumberInput.js";
import { useSelector } from "react-redux";
import ErrorMessage from "../ErrorMessage.js";

const CUSTOMER_BY_SITUATION_MARGIN = 8;

const CustomersContent = ({ location, theme }) => {
  const classes = useRemoveArrowsNumberInput();

  const locationState = location?.state;

  const [filter, setFilter] = useState({
    searchById: locationState?.searchById ?? "",
    searchByText: locationState?.searchByText ?? "",
    situationsSelected: locationState?.situationsSelected ?? [],
  });

  const { customersFinancialSummary, totals, loading, error } = useListCustomersFinancialSummary({
    searchByText: filter.searchByText,
    searchById: filter.searchById,
    situations: filter.situationsSelected,
  });

  const handleSearchText = debounce((value) => {
    setFilter((prev) => ({ ...prev, searchById: "", searchByText: value }));
  });

  const handleSearchId = debounce((value) => {
    setFilter((prev) => ({ ...prev, searchByText: "", searchById: value }));
  });

  return (
    <PageItem>
      <CardPageItem
        raised
        margin={1}
        style={{ marginBottom: 8 }}
      >
        <CardContent>
          <Grid
            container
            spacing={1}
          >
            <Grid
              item
              xs={8}
              sm={8}
              md={8}
            >
              <TextField
                id="nameSearchField"
                label={<FormattedMessage id="app.search.nameemail" />}
                variant="outlined"
                size="small"
                defaultValue={filter.searchByText}
                fullWidth
                onChange={(e) => handleSearchText(e.target.value)}
              />
            </Grid>
            <Grid
              item
              xs={4}
              sm={4}
              md={4}
            >
              <TextField
                id="nameSearchField"
                label={<FormattedMessage id="app.search.id" />}
                variant="outlined"
                size="small"
                type="number"
                defaultValue={filter.searchById}
                fullWidth
                className={classes.input}
                onChange={(e) => handleSearchId(e.target.value)}
                onInput={filterOnlyNumbers}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              <SelectUserSituations
                title="app.situation.customer"
                selectedUserSituations={filter.situationsSelected}
                setSelectedUserSituations={(value) =>
                  setFilter((prev) => ({
                    ...prev,
                    situationsSelected: value,
                  }))
                }
                enableInapt
              />
            </Grid>
          </Grid>
        </CardContent>
      </CardPageItem>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            marginTop: "10%",
          }}
        >
          <CircularProgress />
        </div>
      ) : error ? (
        <ErrorMessage>
          <FormattedMessage
            id={error}
            defaultValue={error}
          />
        </ErrorMessage>
      ) : (
        <>
          <CustomerListBySituationWithAccordion
            data={customersFinancialSummary?.NEW}
            textBar={"app.new"}
            filter={filter}
          />
          <CustomerListBySituationWithAccordion
            data={customersFinancialSummary?.INACTIVE}
            textBar={"app.inactive"}
            filter={filter}
          />
          <CustomerListBySituationWithAccordion
            data={customersFinancialSummary?.ACTIVE}
            textBar={"app.actives"}
            filter={filter}
          />
          <CustomerListBySituationWithAccordion
            data={customersFinancialSummary?.INAPT}
            textBar={"app.inapt"}
            filter={filter}
          />

          <CustomersFooterPageItem
            totals={totals}
            loading={loading}
            maxPageWidth={theme.props.maxPageWidth}
          />
        </>
      )}
    </PageItem>
  );
};

function CustomersFooterPageItem({ totals, loading, maxPageWidth }) {
  const currentUser = useSelector((state) => state.user.user.me);
  const { totalCommission, totalCustody, totalActiveContracts, totalCustomers } = totals;

  return (
    <FixedFooterPageItem
      loading={loading}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        maxWidth: maxPageWidth - CUSTOMER_BY_SITUATION_MARGIN,
      }}
      height={85}
    >
      <FooterItem
        title={
          !totalCustomers ? (
            <FormattedMessage id="app.none" />
          ) : (
            <>
              {`${totalCustomers} `}
              <FormattedMessage id={totalCustomers > 1 ? "app.customers" : "app.customer"} />
            </>
          )
        }
        subtitle={
          <>
            <FormattedMessage id="app.comission.lct" />
            {": "}
            <Number
              value={totalCommission}
              currency="LCT"
              hideValue={currentUser.hideValues}
            />
          </>
        }
        variant="body1"
      />

      <FooterItem
        title={
          <>
            {!totalActiveContracts ? (
              <FormattedMessage id="app.none" />
            ) : (
              <>
                {totalActiveContracts > 1 ? (
                  <FormattedMessage
                    id="app.contracts.quantity"
                    values={{ quantity: totalActiveContracts }}
                  />
                ) : (
                  <FormattedMessage id="app.contracts.quantity.one" />
                )}
              </>
            )}
          </>
        }
        subtitle={
          <>
            <FormattedMessage id="app.custodian.total" />
            {": "}
            <Number
              value={totalCustody}
              currency="LCT"
              hideValue={currentUser.hideValues}
            />
          </>
        }
        variant="body1"
        align="right"
      />
    </FixedFooterPageItem>
  );
}

export default withRouter(withTheme(CustomersContent));
